import EUROPEAN_SOCCER_DATA from './EUROPEAN-SOCCER.json';

export type EuropeanSoccerItem = {
  team: string;
  slug: string;
  teamId?: number;
  stadium?: string;
  city?: string;
  ticketEvolutionCategorySlug?: string | null;
  eventNameIncludes?: string;
  ticketEvolutionStatus?: string;
  seoContentEnabled?: boolean;
  seoExclusionReason?: string;
  seoTitleName?: string;
  visible?: boolean;
};

export type EuropeanSoccerSeoConfig = {
  enabled: boolean;
  contentDirectory: string;
  contentLandingFile: string;
  landingParagraphCount?: number;
  heroImagePath?: string;
  socialImagePath?: string;
};

export type EuropeanSoccerLeague = {
  slug: string;
  title: string;
  country?: string;
  season?: string;
  ticketEvolutionCategorySlug?: string | null;
  ticketEvolutionCategorySlugs?: string[];
  seo?: EuropeanSoccerSeoConfig;
  groups: { title: string; items: EuropeanSoccerItem[] }[];
};

const europeanSoccerLeagues =
  EUROPEAN_SOCCER_DATA.leagues as unknown as EuropeanSoccerLeague[];

const normalizedSlug = (slug: string) => slug.toLowerCase();

const UEFA_EVENT_NAME_EXCLUSIONS = ['world cup', 'national team'];

export const isRelevantUefaEventName = (eventName = '') => {
  const normalizedEventName = eventName.toLowerCase();

  return !UEFA_EVENT_NAME_EXCLUSIONS.some((term) =>
    normalizedEventName.includes(term)
  );
};

export const getEuropeanSoccerLeagues = () => europeanSoccerLeagues;

export const getEuropeanSoccerLeague = (leagueSlug: string) =>
  europeanSoccerLeagues.find(
    (league) => league.slug === normalizedSlug(leagueSlug)
  );

export const getEuropeanSoccerSeoConfig = (leagueSlug: string) =>
  getEuropeanSoccerLeague(leagueSlug)?.seo;

export const getEnabledEuropeanSoccerLeagues = () =>
  europeanSoccerLeagues.filter((league) => league.seo?.enabled);

const getLeagueForCategory = (categorySlug: string) => {
  const normalizedCategorySlug = normalizedSlug(categorySlug);

  return europeanSoccerLeagues.find(
    (item) =>
      item.slug === normalizedCategorySlug ||
      item.ticketEvolutionCategorySlug === normalizedCategorySlug ||
      item.ticketEvolutionCategorySlugs?.includes(normalizedCategorySlug)
  );
};

export const getEuropeanSoccerCompetition = (competitionSlug: string) =>
  europeanSoccerLeagues
    .flatMap((league) => league.groups.flatMap((group) => group.items))
    .find(
      (competition) => competition.slug === normalizedSlug(competitionSlug)
    );

export const getEuropeanSoccerTeam = (leagueSlug: string, teamSlug: string) => {
  const league = getEuropeanSoccerLeague(leagueSlug);

  return league?.groups
    .flatMap((group) => group.items)
    .find(
      (team) => team.slug === normalizedSlug(teamSlug) && team.visible !== false
    );
};

export const getEuropeanSoccerCategorySlugs = (leagueSlug: string) => {
  const league = europeanSoccerLeagues.find(
    (item) => item.slug === normalizedSlug(leagueSlug)
  );

  if (!league) return [];
  if (league.ticketEvolutionCategorySlugs) {
    return league.ticketEvolutionCategorySlugs;
  }

  return league.ticketEvolutionCategorySlug
    ? [league.ticketEvolutionCategorySlug]
    : [];
};

export const getEuropeanSoccerTeamByCategory = (
  categorySlug: string,
  teamSlug: string
) => {
  const league = getLeagueForCategory(categorySlug);

  const team = league?.groups
    .flatMap((group) => group.items)
    .find((item) => item.slug === normalizedSlug(teamSlug));

  return team ?? getEuropeanSoccerCompetition(teamSlug);
};

export const getEuropeanSoccerTeamsByCategory = (categorySlug: string) => {
  const league = getLeagueForCategory(categorySlug);
  if (league) {
    return league.groups.flatMap((group) => group.items);
  }

  const competition = getEuropeanSoccerCompetition(categorySlug);
  return competition ? [competition] : [];
};

export const getEuropeanSoccerOpponents = (
  categorySlug: string,
  teamSlug: string
) => {
  const currentTeam = getEuropeanSoccerTeamByCategory(categorySlug, teamSlug);

  return getEuropeanSoccerTeamsByCategory(categorySlug).filter(
    (team) => team.teamId !== currentTeam?.teamId && team.visible !== false
  );
};
